import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { FaSearch, FaFilter } from 'react-icons/fa';
import apiService from '../services/api';
import CollegeCard from '../components/CollegeCard';
import Loading from '../components/Loading';

const CollegesPage = () => {
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    state: '',
    type: '',
    min_fee: '',
    max_fee: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 12,
    total: 0,
    total_pages: 0
  });

  useEffect(() => {
    fetchColleges();
  }, [pagination.page, filters]);

  const fetchColleges = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        per_page: pagination.per_page,
        ...filters
      };

      const response = await apiService.getColleges(params);
      if (response.data.success) {
        setColleges(response.data.data);
        setPagination(prev => ({ ...prev, ...response.data.pagination }));
      }
    } catch (error) {
      console.error('Error fetching colleges:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchTerm.trim()) {
      fetchColleges();
      return;
    }

    setLoading(true);
    try {
      const response = await apiService.searchColleges(searchTerm);
      if (response.data.success) {
        setColleges(response.data.data);
      }
    } catch (error) {
      console.error('Error searching colleges:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      state: '',
      type: '',
      min_fee: '',
      max_fee: ''
    });
    setSearchTerm('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  return (
    <>
      <Helmet>
        <title>Explore Colleges - EduConsult</title>
        <meta name="description" content="Browse through hundreds of top colleges across India. Find the perfect institution for your academic journey." />
      </Helmet>

      {/* Hero */}
      <section className="hero-pattern pt-32 pb-12 md:pt-40 md:pb-16">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-6">
              Explore <span className="gradient-text">Top Colleges</span>
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              Discover the perfect college that matches your academic goals and aspirations
            </p>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search colleges by name, location..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-12 pr-4 py-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                  />
                </div>
                <button type="submit" className="btn btn-primary px-8">
                  Search
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Filters & Results */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Filters Sidebar */}
            <div className="lg:w-64 flex-shrink-0">
              <div className="bg-gray-50 rounded-xl p-6 sticky top-24">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-bold text-gray-900 flex items-center">
                    <FaFilter className="mr-2" /> Filters
                  </h3>
                  <button
                    onClick={clearFilters}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                  >
                    Clear All
                  </button>
                </div>

                <div className="space-y-6">
                  {/* State Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      State
                    </label>
                    <select
                      value={filters.state}
                      onChange={(e) => handleFilterChange('state', e.target.value)}
                      className="w-full input-field text-sm"
                    >
                      <option value="">All States</option>
                      <option value="Delhi">Delhi</option>
                      <option value="Maharashtra">Maharashtra</option>
                      <option value="Karnataka">Karnataka</option>
                      <option value="Tamil Nadu">Tamil Nadu</option>
                      <option value="Uttar Pradesh">Uttar Pradesh</option>
                      <option value="Rajasthan">Rajasthan</option>
                    </select>
                  </div>

                  {/* College Type Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      College Type
                    </label>
                    <select
                      value={filters.type}
                      onChange={(e) => handleFilterChange('type', e.target.value)}
                      className="w-full input-field text-sm"
                    >
                      <option value="">All Types</option>
                      <option value="government">Government</option>
                      <option value="private">Private</option>
                      <option value="deemed">Deemed University</option>
                    </select>
                  </div>

                  {/* Fee Range */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fee Range (per year)
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="number"
                        placeholder="Min"
                        value={filters.min_fee}
                        onChange={(e) => handleFilterChange('min_fee', e.target.value)}
                        className="input-field text-sm"
                      />
                      <input
                        type="number"
                        placeholder="Max"
                        value={filters.max_fee}
                        onChange={(e) => handleFilterChange('max_fee', e.target.value)}
                        className="input-field text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Colleges Grid */}
            <div className="flex-1">
              <div className="mb-6">
                <p className="text-gray-600">
                  Showing <span className="font-semibold text-gray-900">{colleges.length}</span> of{' '}
                  <span className="font-semibold text-gray-900">{pagination.total}</span> colleges
                </p>
              </div>

              {loading ? (
                <Loading message="Loading colleges..." />
              ) : colleges.length > 0 ? (
                <>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {colleges.map((college) => (
                      <CollegeCard key={college.id} college={college} />
                    ))}
                  </div>

                  {/* Pagination */}
                  {pagination.total_pages > 1 && (
                    <div className="flex justify-center gap-2 mt-12">
                      <button
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                        disabled={pagination.page === 1}
                        className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      
                      {[...Array(pagination.total_pages)].map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setPagination(prev => ({ ...prev, page: index + 1 }))}
                          className={`px-4 py-2 rounded-lg ${
                            pagination.page === index + 1
                              ? 'bg-primary-600 text-white'
                              : 'border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {index + 1}
                        </button>
                      ))}
                      
                      <button
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                        disabled={pagination.page === pagination.total_pages}
                        className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-600 text-lg">No colleges found matching your criteria.</p>
                  <button onClick={clearFilters} className="mt-4 btn btn-primary">
                    Clear Filters
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default CollegesPage;
